import type { PDFDocumentProxy } from 'pdfjs-dist';
import { getDocument } from 'pdfjs-dist';
import React, { useEffect, useMemo, useState } from 'react';

import PDFViewer from './PDFViewer';
import { AppContainer, PdfEmbeddedContainer } from './PdfCuration.style';
import type { FHIRResource, OCRData } from './types';

type Props = {
  embedded?: boolean;
  bboxData: FHIRResource[] | null;
  setBboxData: React.Dispatch<React.SetStateAction<FHIRResource[] | null>>;
  ocrOverride?: OCRData;
  pdfUrl?: string | null;
  activeIndex: number;
};

const PdfCurationViewer: React.FC<Props> = ({
  embedded = false,
  bboxData,
  setBboxData,
  ocrOverride,
  pdfUrl,
  activeIndex,
}) => {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [ocrData, setOcrData] = useState<OCRData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Seed OCR data from override
  useEffect(() => {
    if (ocrOverride) {
      setOcrData(ocrOverride);
    }
  }, [ocrOverride]);

  // Load PDF in embedded mode
  useEffect(() => {
    if (!embedded || !pdfUrl) return;

    setIsLoading(true);
    const loadingTask = getDocument(pdfUrl);

    loadingTask.promise
      .then((doc) => {
        setPdf(doc);
      })
      .catch(() => {
        // Error loading PDF
      })
      .finally(() => setIsLoading(false));
  }, [embedded, pdfUrl]);

  // Active resource (single-element array for the viewer)
  const activeResourceArray: FHIRResource[] | null = useMemo(() => {
    return bboxData && bboxData[0] ? [bboxData[0]] : null;
  }, [bboxData]);

  // Page to auto-scroll to when switching resources
  const activeScrollPage: number | null = useMemo(() => {
    if (!activeResourceArray || activeResourceArray.length === 0) return null;

    const res = activeResourceArray[0];
    const pages: number[] = [];
    res.Resource_data.forEach((item) => {
      item.bboxes.forEach((b) => {
        const p = parseInt(b.page, 10);
        if (!Number.isNaN(p)) pages.push(p);
      });
    });

    if (pages.length === 0) return null;
    return Math.min(...pages);
  }, [activeResourceArray]);

  const isEmbeddedLoading = embedded && !pdf;

  const pdfViewerProps = {
    pdf,
    ocrData,
    bboxData: activeResourceArray,
    isLoading,
    setBboxData: (update: any) => {
      // Only update the active resource inside the full bboxData array
      setBboxData((prev) => {
        if (!prev) return prev;
        if (activeIndex < 0 || activeIndex >= prev.length) return prev;

        const currentActive = prev[activeIndex];
        const singlePrev = [currentActive];

        const nextSingle =
          typeof update === 'function'
            ? (update as (p: FHIRResource[] | null) => FHIRResource[] | null)(
                singlePrev
              )
            : update;

        if (!nextSingle || nextSingle.length === 0) return prev;

        const next = [...prev];
        next[activeIndex] = nextSingle[0];
        return next;
      });
    },
    setOcrData,
    activeScrollPage,
  };

  const Container = embedded ? PdfEmbeddedContainer : AppContainer;

  return (
    <Container>
      {isEmbeddedLoading ? (
        <div>Loading PDF…</div>
      ) : (
        <PDFViewer {...pdfViewerProps} />
      )}
    </Container>
  );
};

export default PdfCurationViewer;
